import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useClientRequests = (filters = {}) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.status,
    filters.urgency,
    filters.payment_terms,
    filters.date_range
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchRequests = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      const response = await clientAPI.getRequests(params);
      setRequests(response.data.results || response.data);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des demandes:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  const createRequest = useCallback(async (requestData) => {
    try {
      const response = await clientAPI.createRequest(requestData);
      // Recharger les demandes après création
      fetchRequests();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la création de la demande:', err);
      let errorMessage = 'Erreur lors de la création de la demande';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (typeof err.response.data === 'object') {
          // Extraire les erreurs de validation
          const errors = Object.entries(err.response.data)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
          errorMessage = errors || JSON.stringify(err.response.data);
        }
      }
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }, [fetchRequests]);

  const updateRequest = useCallback(async (requestId, requestData) => {
    try {
      const response = await clientAPI.updateRequest(requestId, requestData);
      // Recharger les demandes après modification
      fetchRequests();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la demande:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || err.response?.data || 'Erreur lors de la mise à jour de la demande' 
      };
    }
  }, [fetchRequests]);

  const deleteRequest = useCallback(async (requestId) => {
    try {
      await clientAPI.deleteRequest(requestId);
      // Recharger les demandes après suppression
      fetchRequests();
      return { success: true };
    } catch (err) {
      console.error('Erreur lors de la suppression de la demande:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la suppression de la demande' 
      };
    }
  }, [fetchRequests]);

  const publishRequest = useCallback(async (requestId) => {
    try {
      const response = await clientAPI.publishRequest(requestId);
      // Recharger les demandes après publication
      fetchRequests();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la publication de la demande:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la publication de la demande' 
      };
    }
  }, [fetchRequests]);

  const unpublishRequest = useCallback(async (requestId) => {
    try {
      const response = await clientAPI.unpublishRequest(requestId);
      // Recharger les demandes après dépublication
      fetchRequests();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la dépublication de la demande:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la dépublication de la demande' 
      };
    }
  }, [fetchRequests]);

  const cancelRequest = useCallback(async (requestId) => {
    try {
      const response = await clientAPI.cancelRequest(requestId);
      // Recharger les demandes après annulation
      fetchRequests();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de l\'annulation de la demande:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de l\'annulation de la demande' 
      };
    }
  }, [fetchRequests]);

  const getRequestById = useCallback(async (requestId) => {
    try {
      const response = await clientAPI.getRequestById(requestId);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors du chargement de la demande:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors du chargement de la demande' 
      };
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    requests,
    loading,
    error,
    pagination,
    fetchRequests,
    createRequest,
    updateRequest,
    deleteRequest,
    publishRequest,
    unpublishRequest,
    cancelRequest,
    getRequestById,
  };
};

